// ============================================================================
// ONE shared Asia/Dubai display formatter for the whole portal
// (acceptance item 8, 2026-07-19).
//
// Storage is timestamptz (correct) — instants reach the browser as ISO strings
// with a Z/offset. The fault was display-side: raw new Date(x).toLocaleString()
// renders in the DEVICE timezone, so any non-Dubai machine showed shifted
// times. Every user-facing date/time render routes through this module so the
// portal shows Dubai wall-clock time on every machine.
//
// luxon resolves from the repo-root node_modules (a backend dependency); the
// portal already imports it in 40+ components, so this adds no new dependency.
//
// Parsing rules (toDubai):
//   * ISO strings WITH a Z/offset   -> that instant, converted to Dubai
//     (the normal case: pg timestamptz -> Express JSON -> '...Z').
//   * strings WITHOUT an offset     -> interpreted as Dubai wall time. The
//     only naked datetimes in this system are client/admin-authored booking
//     payloads ('yyyy-MM-dd HH:mm:ss'), which are Dubai wall time by contract.
//   * date-only 'yyyy-MM-dd'        -> that calendar day (Dubai midnight), so
//     route/date-input values render as the same day on any device.
//   * Date / epoch millis           -> that instant, converted to Dubai.
//
// NOT routed through here (deliberate):
//   * Number/money toLocaleString('en-US') — numbers, not dates.
//   * Date-picker LABELS for a locally-picked calendar day (e.g.
//     RequestBookingPage.readableWhen) — those are calendar days the user
//     just picked, not instants; local-part round-trips are device-safe.
// ============================================================================
import { DateTime } from 'luxon';

export const DUBAI_ZONE = 'Asia/Dubai';

// Parse any API/date value into a Dubai-zoned luxon DateTime (null if absent
// or unparseable).
export function toDubai(value) {
    if (value === null || value === undefined || value === '') return null;
    let dt;
    if (value instanceof Date) {
        dt = DateTime.fromJSDate(value, { zone: DUBAI_ZONE });
    } else if (typeof value === 'number') {
        dt = DateTime.fromMillis(value, { zone: DUBAI_ZONE });
    } else {
        let s = String(value).trim();
        // pg/mysql-style 'yyyy-MM-dd HH:mm:ss' — make it ISO-parseable.
        if (/^\d{4}-\d{2}-\d{2} \d{1,2}:\d{2}/.test(s)) s = s.replace(' ', 'T');
        dt = DateTime.fromISO(s, { zone: DUBAI_ZONE });
    }
    return dt.isValid ? dt : null;
}

// Generic formatter: any luxon format string, rendered in Asia/Dubai.
// Returns '' for missing/unparseable input — call sites keep their own
// fallback text ('N/A', '—', …).
export function formatDubai(value, fmt) {
    const dt = toDubai(value);
    return dt ? dt.toFormat(fmt) : '';
}

// --- Named formats matching what the UI already shows -----------------------
// (visuals unchanged from the old toLocale* output — only the zone is pinned)
export const dubaiDate = (v) => formatDubai(v, 'dd/MM/yyyy');               // 19/07/2026
export const dubaiTimeHM = (v) => formatDubai(v, 'HH:mm');                  // 14:30
export const dubaiDateTime = (v) => formatDubai(v, 'dd/MM/yyyy, HH:mm:ss'); // en-GB toLocaleString shape
export const dubaiYMD = (v) => formatDubai(v, 'yyyy-MM-dd');                // 2026-07-19

// Today's calendar day in Dubai as 'yyyy-MM-dd' — for date-input/route
// defaults. (new Date().toISOString().slice(0,10) gives the UTC day, which is
// YESTERDAY between midnight and 04:00 Dubai time.)
export const todayDubaiYMD = () => DateTime.now().setZone(DUBAI_ZONE).toFormat('yyyy-MM-dd');

// Whole Dubai calendar days from `value`'s day (plus optional `plusDays`) to
// TODAY's Dubai day. Positive = that day is in the past (overdue), 0 = today,
// negative = in the future. Both endpoints are Asia/Dubai day boundaries —
// never the device's — so "N days overdue" is identical on every machine.
// Zoho date-only strings ('yyyy-MM-dd') are calendar days; toDubai keeps them
// as that Dubai day rather than shifting them through UTC. Returns null for
// missing/unparseable input.
export function dubaiDaysSince(value, plusDays = 0) {
    const dt = toDubai(value);
    if (!dt) return null;
    const day = dt.plus({ days: plusDays }).startOf('day');
    const today = DateTime.now().setZone(DUBAI_ZONE).startOf('day');
    return Math.round(today.diff(day, 'days').days);
}

// The exact payment moment as "09 Aug 2026, 22:09" Dubai time. Zoho's
// created_time carries a basic-format offset ("+0400") that Safari's
// `new Date()` rejects — luxon parses it reliably. Returns null for
// missing/unparseable input so callers can fall back to the day-only date.
export function fmtPaymentMoment(iso) {
    if (!iso) return null;
    const dt = DateTime.fromISO(String(iso), { setZone: true });
    if (!dt.isValid) return null;
    return dt.setZone(DUBAI_ZONE).toFormat('dd MMM yyyy, HH:mm');
}
